import { useCallback, useEffect, useState } from 'react';
import { getMarke, onEinrichtungOffen } from '../api/client';

/**
 * Die Lage der Ersteinrichtung für die Schale — Auftrag `PF-assistent`, Task 11
 * des Plans Systemeinstellungen, Spec Abschnitt 6.2 und 6.5, Fall `E-1653`.
 *
 * **Die Schalenregel in einem Satz:** Solange die Einrichtung `offen` ist,
 * führt die Schale jede Adresse ausser dem Assistenten auf den Assistenten
 * (Anmeldung und Sperrbildschirm liegen ausserhalb der Schale und bleiben, wie
 * sie sind). Der Server sperrt ohnehin (`409 EINRICHTUNG_OFFEN`,
 * `einstellungen/einrichtung.guard.ts`); die Oberfläche erspart dem Anwender,
 * an dieser Sperre der Reihe nach zu scheitern.
 *
 * **Die Quelle ist `GET /api/v1/einstellungen/marke` und nicht der
 * Einrichtungsstand.** Der Stand verlangt `einstellung:manage`; die Marke
 * antwortet jeder Sitzung — und die Schale muss auch einer Buchhalterin sagen
 * können, dass die Installation noch nicht eingerichtet ist, statt sie in
 * lauter Fehlerkästen laufen zu lassen.
 *
 * **Die Lage ist `unbekannt`, solange die Antwort unterwegs ist und wenn sie
 * fehlschlägt — und `unbekannt` verhält sich wie `abgeschlossen`.** Der Abruf
 * ist Vorsicht und keine Voraussetzung: Ein Fehler des Abrufs darf nie eine
 * eingerichtete Installation sperren (dieselbe Wahl wie `marke.ts` beim
 * Firmennamen, und dieselbe wie der Wächter der API, der bei einem eigenen
 * Fehler durchlässt). **Das ist auch die Bestandsprobe:** Auf der eigenen,
 * abgeschlossenen Installation ändert die Schale nichts.
 *
 * **Eine zweite Quelle: jede Anfrage.** Meldet irgendeine Anfrage
 * `EINRICHTUNG_OFFEN` (`api/client.ts`, `onEinrichtungOffen`), gilt die
 * Einrichtung als offen, auch wenn die Marke `unbekannt` war.
 */
export type Einrichtungslage = 'unbekannt' | 'offen' | 'abgeschlossen';

export interface EinrichtungslageZugriff {
  readonly lage: Einrichtungslage;
  /** Liest die Lage neu — nach dem Abschluss, damit die Schale den Assistenten freigibt. */
  readonly aktualisieren: () => void;
}

export function useEinrichtungslage(): EinrichtungslageZugriff {
  const [lage, setLage] = useState<Einrichtungslage>('unbekannt');
  const [zaehler, setZaehler] = useState(0);

  useEffect(() => {
    let verworfen = false;
    void (async () => {
      try {
        const marke = await getMarke();
        if (!verworfen) setLage(marke.einrichtung);
      } catch {
        // Bewusst geschluckt: siehe Kopfkommentar — ein Fehler des Abrufs
        // sperrt nie. Eine schon erkannte offene Einrichtung bleibt offen.
        if (!verworfen) setLage((bisher) => (bisher === 'offen' ? bisher : 'unbekannt'));
      }
    })();
    return () => {
      verworfen = true;
    };
  }, [zaehler]);

  useEffect(() => {
    try {
      return onEinrichtungOffen(() => {
        setLage('offen');
      });
    } catch {
      // Wie beim Abruf oben: Die Weiche ist Vorsicht und keine Voraussetzung.
      // Eine Attrappe des Klienten ohne diese Funktion (mehrere Oberflächentests
      // rendern die ganze Schale) lässt die Schale unverändert.
      return undefined;
    }
  }, []);

  const aktualisieren = useCallback((): void => {
    setZaehler((n) => n + 1);
  }, []);

  return { lage, aktualisieren };
}
